import React, { useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress, createTheme, ThemeProvider } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useFarmData } from '../context/FarmDataContext';

const TraceabilityPage = () => {
    const { farm_id } = useParams();
    const location = useLocation();
    const idToken = location.state?.idToken;
    const { farmData, fetchFarmData, loading } = useFarmData();

    // Get location filters from URL state
    const section_id = location.state?.section_id;
    const plot_id = location.state?.plot_id;
    const crop_id = location.state?.crop_id;

    const darkTheme = createTheme({
        palette: {
            mode: 'dark',
            primary: { main: '#90caf9' },
            secondary: { main: '#f48fb1' },
            background: {
                default: '#121212',
                paper: '#1e1e1e',
            },
            text: {
                primary: '#ffffff',
                secondary: '#b0b0b0',
            },
        },
    });

    React.useEffect(() => {
        if (idToken && !farmData) {
            fetchFarmData(farm_id, idToken);
        }
    }, [farm_id, idToken, farmData, fetchFarmData]);

    const getProductName = (event, activityType) => {
        switch (activityType) {
            case 'fertilizer':
                return event.fertilizer_name_cl || event.fertilizer_name || '';
            case 'fungicide':
                return event.fungicide_name || '';
            case 'insecticide':
                return event.insecticide_name || '';
            case 'herbicide':
                return event.herbicide_name || '';
            default:
                return '';
        }
    };

    const salesRows = useMemo(() => {
        if (!farmData?.sales || !farmData?.harvests || !farmData?.crops || !farmData?.fertilizerEvents) return [];

        // Filter sales based on location
        let filteredSales = farmData.sales;
        
        if (section_id) {
            // Filter by section/zone - sales are linked to crops which have zone_id
            filteredSales = filteredSales.filter(sale => {
                const harvest = farmData.harvests.find(h => h.id === sale.harvest_id);
                const crop = farmData.crops.find(c => c.id === harvest?.crop_id);
                return crop && crop.zone_id === section_id;
            });
        }
        
        if (plot_id) {
            // Filter by specific plot
            filteredSales = filteredSales.filter(sale => {
                const harvest = farmData.harvests.find(h => h.id === sale.harvest_id);
                const crop = farmData.crops.find(c => c.id === harvest?.crop_id);
                return crop && crop.plot_id === plot_id;
            });
        }
        
        // Note: crop_id filtering is disabled for this page as requested

        // Sort sales by date (latest first)
        const sortedSales = filteredSales.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            // Sort by most recent first (descending order)
            return dateB - dateA;
        });

        return sortedSales.map((sale, index) => {
            const harvest = farmData.harvests.find((h) => h.id === sale.harvest_id);
            const crop = farmData.crops.find((c) => c.id === harvest?.crop_id);

            return {
                id: index + 1,
                sale_id: sale.id,
                harvest_id: sale.harvest_id,
                date: sale.date,
                variety_name: crop?.variety_name_cl || crop?.variety_name,
                plant_type: crop?.plant_type_cl || crop?.plant_type,
                sale_amt: sale.sale_amt,
                sale_unit_type: sale.sale_unit_type,
                harvest_amt: sale.harvest_amt,
                harvest_unit_type: sale.harvest_unit_type,
                unit_price: sale.unit_price,
                zone_name: sale.zone?.name || 'Unknown',
                plot_name: sale.plot?.name || 'Unknown',
                crop_start_date: crop?.planting_date || null,
                harvest_date: harvest?.date || null,
            };
        });
    }, [farmData, section_id, plot_id]);

    const activityColumns = [
        { field: 'id', headerName: 'Activity ID', flex: 1, minWidth: 100 },
        { field: 'activity_type', headerName: 'Activity Type', flex: 1, minWidth: 120 },
        { field: 'date', headerName: 'Date', flex: 1, minWidth: 120 },
        { field: 'event_cost', headerName: 'Cost', flex: 1, minWidth: 100 },
        { field: 'product_name', headerName: 'Product Name', flex: 1, minWidth: 150 },
        { field: 'input_id', headerName: 'Input ID', flex: 1, minWidth: 100 },
        { field: 'amt_used', headerName: 'Amount Used', flex: 1, minWidth: 120 },
        { field: 'amt_used_unit_type', headerName: 'Unit Type', flex: 1, minWidth: 100 },
        { field: 'user_id', headerName: 'User ID', flex: 1, minWidth: 100 },
    ];

    const getDetailPanelContent = useCallback(
    ({ row }) => {
        // Step 1: Find the harvest using the row's harvest_id
        const harvest = farmData.harvests.find((h) => h.id === row.harvest_id);
        if (!harvest) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            );
        }

        // Step 2: Find the crop using the harvest's crop_id
        const crop = farmData.crops.find((c) => c.id === harvest.crop_id);
        if (!crop) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            );
        }

        // Use harvest_date directly from the row
        const harvestDate = new Date(row.harvest_date);

        // Common filter function for events
        const filterEventsByDate = (events) =>
            events.filter(
                (event) =>
                    event.plot_id === crop.plot_id &&
                    new Date(event.date) >= new Date(crop.transplant_date) &&
                    new Date(event.date) <= harvestDate
            );

        // Fetch events filtered by date and plot_id
        const fertilizerEvents = filterEventsByDate(farmData.fertilizerEvents || []);
        const fungicideEvents = filterEventsByDate(farmData.fungicideEvents || []);
        const insecticideEvents = filterEventsByDate(farmData.insecticideEvents || []);
        const herbicideEvents = filterEventsByDate(farmData.herbicideEvents || []);

        // Combine all events into one array for rendering
        const activities = [
            ...fertilizerEvents.map((event) => ({
                id: event.id,
                activity_type: 'fertilizer',
                date: event.date,
                event_cost: event.event_cost || 0,
                product_name: getProductName(event, 'fertilizer'),
                input_id: event.input_id || '',
                amt_used: event.amt_used || 0,
                amt_used_unit_type: event.amt_used_unit_type || '',
                user_id: event.user_id || '',
            })),
            ...fungicideEvents.map((event) => ({
                id: event.id,
                activity_type: 'fungicide',
                date: event.date,
                event_cost: event.event_cost || 0,
                product_name: getProductName(event, 'fungicide'),
                input_id: event.input_id || '',
                amt_used: event.amt_used || 0,
                amt_used_unit_type: event.amt_used_unit_type || '',
                user_id: event.user_id || '',
            })),
            ...insecticideEvents.map((event) => ({
                id: event.id,
                activity_type: 'insecticide',
                date: event.date,
                event_cost: event.event_cost || 0,
                product_name: getProductName(event, 'insecticide'),
                input_id: event.input_id || '',
                amt_used: event.amt_used || 0,
                amt_used_unit_type: event.amt_used_unit_type || '',
                user_id: event.user_id || '',
            })),
            ...herbicideEvents.map((event) => ({
                id: event.id,
                activity_type: 'herbicide',
                date: event.date,
                event_cost: event.event_cost || 0,
                product_name: getProductName(event, 'herbicide'),
                input_id: event.input_id || '',
                amt_used: event.amt_used || 0,
                amt_used_unit_type: event.amt_used_unit_type || '',
                user_id: event.user_id || '',
            })),
        ].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            // Sort by most recent first (descending order)
            return dateB - dateA;
        });

            return (
                <Box sx={{ 
                    p: { xs: 2, md: 3 }, 
                    bgcolor: '#181F2A', 
                    overflow: 'auto',
                    borderRadius: 1.5,
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)',
                    m: { xs: 1, md: 2 }
                }}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500, mb: 2 }}>
                        Related Activities
                    </Typography>
                    <Box sx={{ mt: 2, height: '100%', width: '100%', overflow: 'auto'}}>
                        <DataGridPro
                            rows={activities}
                            columns={activityColumns}
                            autoHeight={false}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                            sx={{
                                bgcolor: 'transparent',
                                color: '#fff',
                                border: 'none',
                                '& .MuiDataGrid-columnHeaders': {
                                    bgcolor: '#222C2F !important',
                                    color: '#fff',
                                    fontWeight: 500,
                                    fontSize: 13,
                                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    bgcolor: '#222C2F !important',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    color: '#fff',
                                    fontWeight: 500,
                                    fontSize: 13,
                                },
                                '& .MuiDataGrid-cell': {
                                    bgcolor: 'transparent',
                                    color: '#fff',
                                    fontSize: 12,
                                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                                    py: 0.5,
                                },
                                '& .MuiDataGrid-virtualScroller': {
                                    bgcolor: 'transparent',
                                },
                                '& .MuiDataGrid-row:nth-of-type(odd)': {
                                    bgcolor: 'rgba(255,255,255,0.02)',
                                },
                                '& .MuiDataGrid-row:nth-of-type(even)': {
                                    bgcolor: 'transparent',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    bgcolor: 'rgba(255,255,255,0.04)',
                                },
                                '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                                    color: '#F9AA33',
                                },
                            }}
                        />
                    </Box>
                </Box>
            );
        },
        [farmData, darkTheme]
    );

    const getDetailPanelHeight = useCallback(() => 400, []);

    if (loading || !farmData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <ThemeProvider theme={darkTheme}>
            <Box sx={{ 
                p: { xs: '0 0px 0px 0px', md: 3 }, 
                bgcolor: '#181F2A', 
                height: '100%', 
                width: '100%', 
                overflow: 'auto',
                mt: { xs: 0, md: -1 },
                mb: { xs: 8, md: 0 },
            }}>
                <Box sx={{ 
                    backgroundColor: '#181F2A', 
                    borderRadius: 1.5, 
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)', 
                    p: { xs: '0px 0px 0px 0px', md: '8px 24px 24px 24px' },
                    height: { xs: 'calc(100vh - 180px)', md: 'calc(100vh - 70px)' },
                    overflow: 'auto',
                }}>
                    <DataGridPro
                        rows={salesRows}
                        columns={[
                            { field: 'sale_id', headerName: 'Sale ID', flex: 1, minWidth: 100 },
                            { field: 'harvest_id', headerName: 'Harvest ID', flex: 1, minWidth: 100 },
                            { field: 'date', headerName: 'Date', flex: 1, minWidth: 120 },
                            { field: 'variety_name', headerName: 'Variety Name', flex: 1, minWidth: 120 },
                            { field: 'plant_type', headerName: 'Plant Type', flex: 1, minWidth: 120 },
                            { field: 'sale_amt', headerName: 'Sale Amount', flex: 1, minWidth: 120 },
                            { field: 'sale_unit_type', headerName: 'Sale Unit Type', flex: 1, minWidth: 120 },
                            { field: 'harvest_amt', headerName: 'Harvest Amount', flex: 1, minWidth: 120 },
                            { field: 'harvest_unit_type', headerName: 'Harvest Unit Type', flex: 1, minWidth: 120 },
                            { field: 'unit_price', headerName: 'Unit Price', flex: 1, minWidth: 120 },
                            { field: 'zone_name', headerName: 'Section Name', flex: 1, minWidth: 120 },
                            { field: 'plot_name', headerName: 'Plot Name', flex: 1, minWidth: 120 },
                        ]}
                        getDetailPanelContent={getDetailPanelContent}
                        getDetailPanelHeight={getDetailPanelHeight}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        pagination
                        disableSelectionOnClick
                        sx={{
                            height: '100%',
                            bgcolor: 'transparent',
                            color: '#fff',
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: '#222C2F !important',
                                color: '#fff',
                                fontWeight: 500,
                                fontSize: { xs: 12, md: 13 },
                                borderBottom: '1px solid rgba(255,255,255,0.12)',
                            },
                            '& .MuiDataGrid-columnHeader': {
                                bgcolor: '#222C2F !important',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                color: '#fff',
                                fontWeight: 500,
                                fontSize: { xs: 12, md: 13 },
                            },
                            '& .MuiDataGrid-cell': {
                                bgcolor: 'transparent',
                                color: '#fff',
                                fontSize: { xs: 11, md: 12 },
                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                                py: 0.5,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                bgcolor: 'transparent',
                            },
                            '& .MuiDataGrid-row': {
                                height: { xs: 40, md: 48 },
                            },
                            '& .MuiDataGrid-row:nth-of-type(odd)': {
                                bgcolor: 'rgba(255,255,255,0.02)',
                            },
                            '& .MuiDataGrid-row:nth-of-type(even)': {
                                bgcolor: 'transparent',
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: 'rgba(255,255,255,0.04)',
                            },
                            '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                                color: '#F9AA33',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                bgcolor: '#222C2F',
                                color: '#fff',
                                borderTop: '1px solid rgba(255,255,255,0.12)',
                            },
                        }}
                    />
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default TraceabilityPage;
